import { useState, useEffect, useRef } from 'react';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LightBulbIcon,
    InformationCircleIcon,
    BookOpenIcon,
} from '@heroicons/react/24/outline';

import ssLogin           from '../assets/screenshots/login.png';
import ssNav             from '../assets/screenshots/nav.png';
import ssOrdersList      from '../assets/screenshots/orders_list.png';
import ssOrderDetail     from '../assets/screenshots/order_detail.png';
import ssCreateOrder     from '../assets/screenshots/create_order.png';
import ssAssignPicker    from '../assets/screenshots/assign_picker.png';
import ssCollectionOrder from '../assets/screenshots/collection_order.png';
import ssPinModal        from '../assets/screenshots/pin_modal.png';
import ssDrivers         from '../assets/screenshots/drivers.png';
import ssOrdersActions   from '../assets/screenshots/orders_actions.png';
import ssPickers         from '../assets/screenshots/pickers.png';
import ssCpoints         from '../assets/screenshots/collection_points.png';
import ssCpModal         from '../assets/screenshots/add_cp_modal.png';
import ssFees            from '../assets/screenshots/fees.png';

const SCREENSHOTS: Record<string, string> = {
    login:            ssLogin,
    nav:              ssNav,
    orders_list:      ssOrdersList,
    order_detail:     ssOrderDetail,
    create_order:     ssCreateOrder,
    assign_picker:    ssAssignPicker,
    collection_order: ssCollectionOrder,
    pin_modal:        ssPinModal,
    drivers:          ssDrivers,
    orders_actions:   ssOrdersActions,
    pickers:          ssPickers,
    cpoints:          ssCpoints,
    cpmodal:          ssCpModal,
    fees:             ssFees,
};

// ── Types ─────────────────────────────────────────────────────────────────────
type Step = { action: string; detail?: string };
type Callout = { type: 'tip' | 'warn' | 'note'; text: string };
type Block =
    | { kind: 'body'; text: string }
    | { kind: 'steps'; items: Step[] }
    | { kind: 'bullets'; items: string[] }
    | { kind: 'callout'; callout: Callout }
    | { kind: 'checklist'; items: string[] }
    | { kind: 'statuses'; rows: [string, string, string][] };

type SubSectionType = { id: string; number: string; title: string; screenshot?: string; blocks: Block[] };

type Section = {
    id: string;
    number: string;
    title: string;
    screenshot?: string;
    blocks: Block[];
    sub?: SubSectionType[];
};

// ── Data ──────────────────────────────────────────────────────────────────────
const SECTIONS: Section[] = [
    {
        id: 'logging-in',
        number: '1',
        title: 'Logging In',
        screenshot: 'login',
        blocks: [
            { kind: 'body', text: 'Open your web browser and go to the portal URL provided by your administrator.' },
            {
                kind: 'steps',
                items: [
                    { action: 'Type your email address into the Email field.' },
                    { action: 'Type your password into the Password field.' },
                    { action: 'Click the Sign in button.' },
                ],
            },
            { kind: 'body', text: 'If your credentials are correct you will be taken to the Overview screen. If login fails, a red error message appears — check your email and password and try again.' },
            { kind: 'callout', callout: { type: 'tip', text: "If you have forgotten your password, click 'Forgot your password?' below the password field." } },
        ],
    },
    {
        id: 'navigation',
        number: '2',
        title: 'The Main Navigation',
        screenshot: 'nav',
        blocks: [
            { kind: 'body', text: 'The dark left sidebar is always visible. Click any item to navigate there:' },
            {
                kind: 'bullets',
                items: [
                    "Overview — home dashboard showing today's orders and activity",
                    'Orders & Fulfilment — Orders, Deliveries, Collections, Delivery Dispatch',
                    'Catalogue — Products, Categories, Specials  (Admins only)',
                    'Stores — view store locations',
                    'Operations — Drivers, Pickers, Live Map, Delivery Options, Collection Points',
                    'Customers — view and manage customer accounts',
                    'Analytics — reports and revenue charts  (Admins only)',
                ],
            },
            { kind: 'body', text: 'The Create User button at the bottom is visible to Admins only. Click your name in the top right to log out.' },
            { kind: 'callout', callout: { type: 'note', text: 'If a menu item is missing from your sidebar, your account does not have access to it. Contact your administrator.' } },
        ],
    },
    {
        id: 'statuses',
        number: '3',
        title: 'Understanding Order Statuses',
        blocks: [
            { kind: 'body', text: 'Every order has a coloured status badge showing exactly where it is in the process. Refer to these tables whenever you are unsure what a status means or what to do next.' },
        ],
        sub: [
            {
                id: 'statuses-delivery',
                number: '3.1',
                title: 'Delivery Order Statuses',
                blocks: [
                    {
                        kind: 'statuses',
                        rows: [
                            ['CREATED',           'Order placed, payment pending',         'No action — wait for payment'],
                            ['AWAITING_PAYMENT',   'Payment started, not yet confirmed',    'No action — customer must pay'],
                            ['PAYMENT_SUCCEEDED',  'Payment confirmed',                     'No action — moves to packing'],
                            ['PAYMENT_FAILED',     'Payment failed',                        'Contact customer to retry'],
                            ['PAYMENT_CANCELLED',  'Customer cancelled payment',            'Contact customer if needed'],
                            ['AWAITING_PACKING',   'Paid and queued for packing',           'Assign a picker immediately'],
                            ['PACKER_ASSIGNED',    'Picker is actively packing',            'Monitor progress'],
                            ['PACKING_COMPLETE',   'All items packed and verified',         'Assign a driver'],
                            ['ASSIGN_DRIVER',      'Waiting for a driver',                  'Assign a driver immediately'],
                            ['DRIVER_ASSIGNED',    'Driver assigned, not yet collected',    'Confirm driver is aware'],
                            ['ON_ROUTE',           'Driver en route to customer',           'Monitor via Delivery Tracking tab'],
                            ['DELIVERY_FAILED',    'Delivery unsuccessful',                 'Contact customer, reassign driver'],
                            ['DELIVERY_COMPLETE',  'Order delivered and confirmed',         'No action — complete'],
                            ['ORDER_CANCELLED',    'Order voided',                          'No action — wallet refunded'],
                        ],
                    },
                ],
            },
            {
                id: 'statuses-collection',
                number: '3.2',
                title: 'Collection Order Statuses',
                blocks: [
                    {
                        kind: 'statuses',
                        rows: [
                            ['AWAITING_PACKING',      'Paid and queued for packing',     'Assign a picker immediately'],
                            ['PACKER_ASSIGNED',       'Picker is actively packing',      'Monitor progress'],
                            ['PACKING_COMPLETE',      'Items packed',                    'Click Complete Packing'],
                            ['READY_FOR_COLLECTION',  'Customer notified to collect',    'Stage at collection point'],
                            ['COLLECTION_COMPLETE',   'Customer has collected',          'No action — complete'],
                        ],
                    },
                    { kind: 'callout', callout: { type: 'note', text: 'Collection orders skip driver assignment entirely. Once packing is complete the status moves straight to READY_FOR_COLLECTION and the customer is notified automatically.' } },
                ],
            },
        ],
    },
    {
        id: 'shift',
        number: '4',
        title: 'Starting Your Shift',
        screenshot: 'orders_list',
        blocks: [
            { kind: 'body', text: 'Every time you log in for a shift, work through this checklist before taking any other action.' },
            {
                kind: 'checklist',
                items: [
                    'Click Orders & Fulfilment → Orders and open the Open tab',
                    'Check for any orders at AWAITING_PACKING (blue) — assign a picker to each',
                    'Check for any orders at ASSIGN_DRIVER or PACKING_COMPLETE (indigo) — assign a driver to each',
                    'Check for any orders at READY_FOR_COLLECTION (teal) — confirm items are staged at the collection point',
                    'Check for any orders at DELIVERY_FAILED (red) — contact those customers immediately',
                    'Go to Operations → Drivers and confirm at least one driver is available',
                    'Go to Operations → Pickers and confirm pickers are assigned to the correct stores',
                ],
            },
            { kind: 'callout', callout: { type: 'tip', text: 'The Orders page updates in real time. New orders appear automatically — you never need to refresh the page.' } },
        ],
    },
    {
        id: 'orders-page',
        number: '5',
        title: 'The Orders Page',
        screenshot: 'orders_actions',
        blocks: [
            { kind: 'body', text: 'The Orders page is where you will spend most of your time. It has two tabs — Open and Delivered — plus filters at the top.' },
            {
                kind: 'bullets',
                items: [
                    'Search bar — find orders by reference number or customer name',
                    'All Fulfilment — filter by Delivery or Collection',
                    'All Types — filter between Ecom (app) and Chat (manual WhatsApp) orders',
                    'All Statuses — filter by one or more statuses',
                    '+ Capture New Order — pink button in the top right to create a manual order',
                ],
            },
            { kind: 'body', text: 'Click the three-dot ⋮ menu on any order row for quick actions. Click anywhere on the row to open the full order detail page.' },
        ],
        sub: [
            {
                id: 'order-detail-page',
                number: '5.1',
                title: 'The Order Detail Page',
                screenshot: 'order_detail',
                blocks: [
                    { kind: 'body', text: 'Opening an order shows five tabs:' },
                    {
                        kind: 'bullets',
                        items: [
                            'Order Overview — financial summary, customer details, delivery address or collection point, line items',
                            'Order Details — the editable product table',
                            'Payments — all payment records and wallet transactions',
                            'Status & Logs — full timestamped history of every status change',
                            'Delivery Tracking — live driver map (delivery orders only)',
                        ],
                    },
                    { kind: 'body', text: 'The action buttons in the top right change depending on the current order status — Print Order, Assign Packer, Complete Packing, Create Delivery, or Complete Collection.' },
                ],
            },
        ],
    },
    {
        id: 'app-order',
        number: '6',
        title: 'Processing a New App Order',
        blocks: [
            { kind: 'body', text: 'When a customer places an order through the mobile app, it appears in the portal automatically. You only need to act once it reaches AWAITING_PACKING.' },
            { kind: 'callout', callout: { type: 'warn', text: 'Do not leave an order at AWAITING_PACKING without assigning a picker. The customer is waiting.' } },
        ],
        sub: [
            {
                id: 'app-order-what',
                number: '6.1',
                title: 'What to watch for',
                blocks: [
                    {
                        kind: 'bullets',
                        items: [
                            'A new order at AWAITING_PACKING (blue badge) — payment confirmed, packing must begin',
                            'An order stuck at AWAITING_PAYMENT for a long time — customer may not have completed payment',
                            "An order at PAYMENT_FAILED — customer's payment did not go through",
                        ],
                    },
                ],
            },
            {
                id: 'app-order-do',
                number: '6.2',
                title: 'What to do',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click on the order row to open the order detail page.' },
                            { action: 'Review the order.', detail: 'Check customer details and confirm the fulfilment type (Delivery or Collection) is correct.' },
                            { action: 'Click Assign Packer to begin the packing process.', detail: 'See Section 8 for the full packing steps.' },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'whatsapp',
        number: '7',
        title: 'Capturing a WhatsApp Order Manually',
        screenshot: 'create_order',
        blocks: [
            { kind: 'body', text: 'When a customer sends an order via WhatsApp, you must capture it manually in the portal. It is created as a Chat order and follows the same packing and fulfilment process as an app order.' },
            {
                kind: 'steps',
                items: [
                    { action: 'Click the + Capture New Order button', detail: 'The pink button in the top right of the Orders page.' },
                    { action: 'Enter the order Reference.', detail: "Use the customer's name and today's date — e.g. Jane-Smith-280526." },
                    { action: 'Enter the Parcel Count.' },
                    { action: 'Select the Store Location from the dropdown.' },
                    { action: 'Select the Fulfilment Type by clicking an option card.', detail: 'Collection, Express, Standard, or any custom option. The selected card shows a pink border.' },
                    { action: 'If Collection is selected, click the correct collection point card from the list that appears.' },
                    { action: 'Type the order details into the Order Details / Notes field.', detail: 'Include the full item list from the WhatsApp message and any special instructions.' },
                    { action: 'Search for the customer by name and click them from the dropdown.', detail: 'If the customer is new, click + New customer and fill in their details.' },
                    { action: 'Click Create order.' },
                ],
            },
            { kind: 'callout', callout: { type: 'note', text: 'The Fulfilment Type must be selected before submitting. For Collection orders, a collection point must also be selected. The Create order button stays greyed out until both are chosen.' } },
            { kind: 'callout', callout: { type: 'warn', text: 'The fulfilment type cannot be changed once the order is created. Always double-check it before submitting.' } },
        ],
    },
    {
        id: 'packing',
        number: '8',
        title: 'Packing an Order',
        blocks: [
            { kind: 'body', text: 'Every order must be physically packed before it can proceed. Packing has two steps: assigning a picker, then confirming packing is complete.' },
        ],
        sub: [
            {
                id: 'packing-assign',
                number: '8.1',
                title: 'Assigning a Picker',
                screenshot: 'assign_picker',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Open the order detail page.' },
                            { action: 'Click Print Order (pink, top right)', detail: 'Print the sheet and hand it to the picker so they know exactly what to collect.' },
                            { action: 'Click Assign Packer (black, top right).' },
                            { action: 'Select the picker from the dropdown.', detail: 'Only pickers assigned to the same store appear in the list.' },
                            { action: 'Click Confirm.' },
                        ],
                    },
                    { kind: 'body', text: 'The status changes to PACKER_ASSIGNED (purple badge).' },
                    { kind: 'callout', callout: { type: 'warn', text: 'If the picker dropdown is empty, no pickers are assigned to this store. Go to Operations → Pickers, add a picker for the correct store, then return to the order.' } },
                ],
            },
            {
                id: 'packing-complete',
                number: '8.2',
                title: 'Completing Packing',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Confirm with the picker that all items are packed and match the order sheet.' },
                            { action: 'Click Complete Packing (top right).' },
                            { action: 'Review the checklist in the modal then click Confirm.' },
                        ],
                    },
                    {
                        kind: 'bullets',
                        items: [
                            'Delivery orders — status moves to ASSIGN_DRIVER, Create Delivery button appears. See Section 9.',
                            'Collection orders — status moves to READY_FOR_COLLECTION, customer notified. See Section 10.',
                        ],
                    },
                    { kind: 'callout', callout: { type: 'note', text: 'Never click Complete Packing unless items have been physically verified. For collection orders this sends an immediate notification to the customer.' } },
                ],
            },
        ],
    },
    {
        id: 'delivery',
        number: '9',
        title: 'Dispatching a Delivery Order',
        blocks: [],
        sub: [
            {
                id: 'delivery-assign',
                number: '9.1',
                title: 'Assigning a Driver',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Open the order detail page.' },
                            { action: 'Click Create Delivery (top right).' },
                            { action: "Click on a driver's name to select them." },
                            { action: 'Confirm the delivery address is correct.' },
                            { action: 'Click Submit.' },
                        ],
                    },
                    { kind: 'body', text: 'The status changes to DRIVER_ASSIGNED. The driver can see the order in their app.' },
                    { kind: 'callout', callout: { type: 'warn', text: 'Only assign drivers who are at or near the store. A far-away driver will significantly delay the delivery.' } },
                    { kind: 'callout', callout: { type: 'tip', text: 'If no drivers appear, all may be on active deliveries. Go to Operations → Drivers to check.' } },
                ],
            },
            {
                id: 'delivery-track',
                number: '9.2',
                title: 'Tracking a Driver',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Open the order detail page.' },
                            { action: 'Click the Delivery Tracking tab.' },
                        ],
                    },
                    { kind: 'body', text: "A map shows the driver's position and route to the customer. If location has not updated yet, a waiting overlay appears." },
                    { kind: 'callout', callout: { type: 'tip', text: 'The Status & Logs tab shows a full timestamped record of every status change.' } },
                ],
            },
            {
                id: 'delivery-failed',
                number: '9.3',
                title: 'Handling a Failed Delivery',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Open the order detail page.' },
                            { action: 'Click the Status & Logs tab', detail: 'Read the failure reason recorded by the driver.' },
                            { action: 'Contact the customer using the phone number on the Order Overview tab.' },
                            { action: 'Click Create Delivery to assign a new driver.' },
                            { action: 'If the customer no longer wants the order, cancel it.', detail: 'See Section 11.' },
                        ],
                    },
                    { kind: 'callout', callout: { type: 'warn', text: 'Do not leave an order at DELIVERY_FAILED without taking action. The customer has paid and is waiting.' } },
                ],
            },
        ],
    },
    {
        id: 'collection',
        number: '10',
        title: 'Handling a Collection Order',
        blocks: [],
        sub: [
            {
                id: 'collection-ready',
                number: '10.1',
                title: 'Order Ready for Collection',
                screenshot: 'collection_order',
                blocks: [
                    { kind: 'body', text: 'When packing is complete, the status moves to READY_FOR_COLLECTION and the customer receives a push notification.' },
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Open the order detail page to confirm which collection point the customer selected.', detail: 'The Collection Point card on the Order Overview tab shows the name, address, and instructions.' },
                            { action: 'Take the packed items to the collection point and stage them there.' },
                            { action: 'Ensure the order reference number is visible on the package.' },
                        ],
                    },
                    { kind: 'callout', callout: { type: 'tip', text: 'Check the Open tab regularly so you know how many customers to expect and can ensure orders are staged in advance.' } },
                ],
            },
            {
                id: 'collection-arrive',
                number: '10.2',
                title: 'When the Customer Arrives',
                screenshot: 'pin_modal',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Ask the customer for their order reference and 4-digit collection PIN.', detail: 'Both are visible in their app under order history.' },
                            { action: 'Find the order using the search bar on the Orders page.' },
                            { action: 'Match the reference to the staged package.' },
                            { action: 'Click Complete Collection (pink, top right).' },
                            { action: 'Type the 4-digit PIN the customer provides into the input field.' },
                            { action: 'Click Confirm.' },
                        ],
                    },
                    { kind: 'body', text: 'If the PIN is correct — the status changes to COLLECTION_COMPLETE (green badge). If the PIN is wrong — a red error appears and the order is not updated.' },
                    { kind: 'callout', callout: { type: 'warn', text: "Do not release the order without the correct PIN. If the customer cannot find their PIN, ask them to open their app and go to their Orders section." } },
                ],
            },
        ],
    },
    {
        id: 'cancel',
        number: '11',
        title: 'Cancelling an Order',
        screenshot: 'orders_actions',
        blocks: [
            { kind: 'body', text: 'Only cancel an order when the customer requests it or when the order cannot be fulfilled.' },
            {
                kind: 'steps',
                items: [
                    { action: 'Find the order on the Orders page.' },
                    { action: 'Click the three-dot ⋮ menu on the right of the order row.' },
                    { action: 'Click Cancel Order.' },
                    { action: 'Click Confirm in the modal.' },
                ],
            },
            { kind: 'body', text: 'The status changes to ORDER_CANCELLED and any wallet credit is automatically refunded.' },
            { kind: 'callout', callout: { type: 'note', text: 'Orders at ON_ROUTE, DELIVERY_COMPLETE, COLLECTION_COMPLETE, or ORDER_CANCELLED cannot be cancelled.' } },
            { kind: 'callout', callout: { type: 'tip', text: 'If an order is ON_ROUTE and must be cancelled, contact the driver directly first.' } },
        ],
    },
    {
        id: 'drivers',
        number: '12',
        title: 'Managing Drivers',
        screenshot: 'drivers',
        blocks: [
            { kind: 'body', text: 'Navigate to Operations → Drivers. The page lists all drivers with their name, vehicle details, current status, store assignment, and last active time.' },
        ],
        sub: [
            {
                id: 'drivers-add',
                number: '12.1',
                title: 'Adding a Driver',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click + Add Driver in the top right.' },
                            { action: "Enter the driver's name, vehicle model, and number plate." },
                            { action: 'Select the store this driver is assigned to.' },
                            { action: 'Click Save.' },
                        ],
                    },
                    { kind: 'body', text: 'The driver appears with an Awaiting Order status. A login account for their app is created automatically.' },
                    { kind: 'callout', callout: { type: 'note', text: 'A driver can only handle one active delivery at a time. Drivers ON_ROUTE do not appear in the available drivers list.' } },
                ],
            },
        ],
    },
    {
        id: 'pickers-page',
        number: '13',
        title: 'Managing Pickers',
        screenshot: 'pickers',
        blocks: [
            { kind: 'body', text: 'Navigate to Operations → Pickers. The page lists all pickers with their name, store assignment, and date added.' },
        ],
        sub: [
            {
                id: 'pickers-add',
                number: '13.1',
                title: 'Adding a Picker',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click New Picker in the top right.' },
                            { action: "Enter the picker's first and last name." },
                            { action: 'Select the store they work at.' },
                            { action: 'Click Save.' },
                        ],
                    },
                    { kind: 'body', text: 'The picker appears immediately and is available for assignment at that store.' },
                    { kind: 'callout', callout: { type: 'warn', text: "If no pickers appear when assigning an order, no pickers are assigned to that order's store. Go to Operations → Pickers and add one." } },
                ],
            },
        ],
    },
    {
        id: 'collection-points-page',
        number: '14',
        title: 'Managing Collection Points',
        screenshot: 'cpoints',
        blocks: [
            { kind: 'body', text: 'Navigate to Operations → Collection Points. Only active collection points are shown to customers at checkout.' },
        ],
        sub: [
            {
                id: 'cpoints-add',
                number: '14.1',
                title: 'Adding a Collection Point',
                screenshot: 'cpmodal',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click + Add Collection Point in the top right.' },
                            { action: "Enter the collection point name.", detail: "e.g. 'Main Store', 'Mall Kiosk', 'Locker Bay A'." },
                            { action: 'Enter the street address, suburb, and city.' },
                            { action: 'Enter Collection Instructions.', detail: "Describe exactly where the customer should go — e.g. 'Enter through the side gate and ring the bell.'" },
                            { action: 'Click Add Collection Point.' },
                        ],
                    },
                    { kind: 'body', text: 'The new point appears as ACTIVE and is immediately visible to customers at checkout.' },
                    { kind: 'callout', callout: { type: 'tip', text: "Use Deactivate rather than Remove for temporary closures. Deactivation is reversible — removal is permanent." } },
                ],
            },
        ],
    },
    {
        id: 'fees-page',
        number: '15',
        title: 'Delivery Fees & Free Delivery',
        screenshot: 'fees',
        blocks: [
            { kind: 'body', text: 'Navigate to Operations → Delivery Options.' },
        ],
        sub: [
            {
                id: 'fees-update',
                number: '15.1',
                title: 'Updating Delivery Fees',
                blocks: [
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click Update Fees in the top right.' },
                            { action: 'Enter new fee amounts in Rands for Standard and Express Delivery.' },
                            { action: 'Click Update Fees.' },
                        ],
                    },
                    { kind: 'body', text: 'New fees are active immediately. Previous fees are archived automatically.' },
                ],
            },
            {
                id: 'fees-free',
                number: '15.2',
                title: 'Setting the Free Delivery Threshold',
                blocks: [
                    { kind: 'body', text: 'The Free Delivery Threshold card controls the minimum cart value a customer must reach before the free delivery option unlocks in the app.' },
                    {
                        kind: 'steps',
                        items: [
                            { action: 'Click Edit Threshold on the Free Delivery card.' },
                            { action: 'Enter the minimum cart value in Rands — e.g. 200.00 for R200.00.' },
                            { action: 'Click Save Threshold.' },
                        ],
                    },
                    { kind: 'body', text: 'Use the toggle switch on the card to enable or disable free delivery entirely. When disabled the progress bar and free delivery option do not appear in the app.' },
                ],
            },
        ],
    },
    {
        id: 'troubleshooting',
        number: '16',
        title: 'Troubleshooting',
        blocks: [
            { kind: 'body', text: 'Use this section as a quick reference when something unexpected happens.' },
        ],
        sub: [
            { id: 'ts-1', number: '', title: 'No pickers appear when assigning an order', blocks: [{ kind: 'body', text: 'No pickers are assigned to this store. Go to Operations → Pickers, add a picker for the correct store, then return to the order.' }] },
            { id: 'ts-2', number: '', title: 'No drivers appear when assigning a delivery', blocks: [{ kind: 'body', text: 'All drivers are on active deliveries or inactive. Go to Operations → Drivers to check. Wait for one to become available or contact a driver directly.' }] },
            { id: 'ts-3', number: '', title: 'Complete Packing button is not showing', blocks: [{ kind: 'body', text: 'Check the status badge. If it shows ASSIGN_DRIVER or higher, packing has already been marked complete.' }] },
            { id: 'ts-4', number: '', title: 'A collection order is not in the Open tab', blocks: [{ kind: 'body', text: 'Clear all status filters. Also confirm the status is READY_FOR_COLLECTION — if it shows PACKER_ASSIGNED, packing has not been completed yet.' }] },
            { id: 'ts-5', number: '', title: 'A customer says their PIN is wrong', blocks: [{ kind: 'body', text: 'Ask them to open their app, go to Orders, and read the PIN from the order detail screen. Do not release the order without the correct PIN.' }] },
            { id: 'ts-6', number: '', title: 'A collection point is not showing in the app', blocks: [{ kind: 'body', text: 'Go to Operations → Collection Points. Check the badge shows ACTIVE. If it shows INACTIVE, click Activate on the card.' }] },
        ],
    },
];

// ── Callout ───────────────────────────────────────────────────────────────────
function Callout({ callout }: { callout: Callout }) {
    const map = {
        tip:  { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', label: 'Tip',       Icon: LightBulbIcon,          iconColor: 'text-emerald-500' },
        warn: { bg: 'bg-amber-50 border-amber-200',     text: 'text-amber-900',  label: 'Important', Icon: ExclamationTriangleIcon, iconColor: 'text-amber-500'   },
        note: { bg: 'bg-pink-50 border-pink-200',       text: 'text-pink-900',   label: 'Note',      Icon: InformationCircleIcon,   iconColor: 'text-[#e6186c]'   },
    };
    const s = map[callout.type];
    return (
        <div className={`flex gap-3 rounded-lg border px-4 py-3 text-sm ${s.bg}`}>
            <s.Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${s.iconColor}`} />
            <p className={s.text}><span className="font-semibold">{s.label}: </span>{callout.text}</p>
        </div>
    );
}

// ── Screenshot ────────────────────────────────────────────────────────────────
function Screenshot({ ssKey, alt }: { ssKey: string; alt: string }) {
    const src = SCREENSHOTS[ssKey];
    if (!src) return null;
    return (
        <div className="overflow-hidden rounded-xl border border-stone-200 shadow-sm">
            <div className="bg-[#e6186c] px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1">
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                </div>
                <p className="text-xs font-medium text-white/90 ml-1">{alt}</p>
            </div>
            <img src={src} alt={alt} className="w-full object-contain bg-stone-50" />
        </div>
    );
}

// ── Block renderer ────────────────────────────────────────────────────────────
function RenderBlock({ block }: { block: Block }) {
    if (block.kind === 'body') return (
        <p className="text-sm text-stone-600 leading-relaxed">{block.text}</p>
    );

    if (block.kind === 'callout') return <Callout callout={block.callout} />;

    if (block.kind === 'bullets') return (
        <ul className="space-y-1.5">
            {block.items.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-stone-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#e6186c] flex-shrink-0" />
                    {item}
                </li>
            ))}
        </ul>
    );

    if (block.kind === 'steps') return (
        <ol className="space-y-2">
            {block.items.map((s, i) => (
                <li key={i} className="flex gap-3">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e6186c] text-xs font-bold text-white">
                        {i + 1}
                    </span>
                    <div className="pt-0.5">
                        <span className="text-sm font-medium text-stone-800">{s.action}</span>
                        {s.detail && <p className="mt-0.5 text-xs text-stone-500 italic">{s.detail}</p>}
                    </div>
                </li>
            ))}
        </ol>
    );

    if (block.kind === 'checklist') return (
        <div className="space-y-1.5">
            {block.items.map((item, i) => (
                <ChecklistItem key={i} text={item} />
            ))}
        </div>
    );

    if (block.kind === 'statuses') return (
        <div className="overflow-hidden rounded-lg border border-stone-200">
            <table className="w-full text-xs">
                <thead>
                <tr className="bg-[#e6186c]">
                    {['Status', 'What it means', 'Action required'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-white">{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {block.rows.map(([st, desc, act], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-stone-50' : 'bg-white'}>
                        <td className="px-3 py-2 font-mono font-semibold text-stone-800">{st}</td>
                        <td className="px-3 py-2 text-stone-600">{desc}</td>
                        <td className="px-3 py-2 text-stone-600">{act}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );

    return null;
}

function ChecklistItem({ text }: { text: string }) {
    const [checked, setChecked] = useState(false);
    return (
        <button
            onClick={() => setChecked(c => !c)}
            className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                checked ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-white hover:bg-stone-50'
            }`}
        >
            <div className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-all ${
                checked ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'
            }`}>
                {checked && <CheckCircleIcon className="h-3 w-3 text-white" />}
            </div>
            <span className={`leading-snug transition-all ${checked ? 'text-stone-400 line-through' : 'text-stone-700'}`}>
                {text}
            </span>
        </button>
    );
}

// ── Sub-section accordion ─────────────────────────────────────────────────────
function SubSection({ sub, defaultOpen = false }: {
    sub: SubSectionType;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-stone-50"
            >
                <div className="flex items-center gap-3">
                    {sub.number && (
                        <span className="flex h-6 w-10 items-center justify-center rounded-md bg-stone-100 text-xs font-bold text-stone-500">
                            {sub.number}
                        </span>
                    )}
                    <span className="font-semibold text-stone-800">{sub.title}</span>
                </div>
                {open
                    ? <ChevronDownIcon className="h-4 w-4 text-stone-400" />
                    : <ChevronRightIcon className="h-4 w-4 text-stone-400" />
                }
            </button>
            {open && (
                <div className="space-y-4 border-t border-stone-100 px-5 py-5">
                    {sub.screenshot && <Screenshot ssKey={sub.screenshot} alt={sub.title} />}
                    {sub.blocks.map((b, i) => <RenderBlock key={i} block={b} />)}
                </div>
            )}
        </div>
    );
}

// ── Main section card ─────────────────────────────────────────────────────────
function SectionCard({ section }: { section: Section }) {
    const [open, setOpen] = useState(true);
    const ref = useRef<HTMLDivElement>(null);
    const hasContent = section.blocks.length > 0 || (section.sub && section.sub.length > 0);

    return (
        <div ref={ref} id={section.id} className="scroll-mt-6">
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <button
                    onClick={() => setOpen(o => !o)}
                    className="flex w-full items-center justify-between px-6 py-5 text-left transition-colors hover:bg-stone-50"
                >
                    <div className="flex items-center gap-4">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e6186c] text-sm font-bold text-white shadow-sm">
                            {section.number}
                        </span>
                        <h2 className="text-base font-semibold text-stone-900">{section.title}</h2>
                    </div>
                    <div className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                        open ? 'bg-[#e6186c] text-white' : 'bg-stone-100 text-stone-400'
                    }`}>
                        {open
                            ? <ChevronDownIcon className="h-4 w-4" />
                            : <ChevronRightIcon className="h-4 w-4" />
                        }
                    </div>
                </button>

                {open && hasContent && (
                    <div className="border-t border-stone-100 px-6 py-6 space-y-5">
                        {section.screenshot && (
                            <Screenshot ssKey={section.screenshot} alt={section.title} />
                        )}
                        {section.blocks.map((b, i) => <RenderBlock key={i} block={b} />)}
                        {section.sub && section.sub.length > 0 && (
                            <div className="space-y-3">
                                {section.sub.map((sub, i) => (
                                    <SubSection key={sub.id} sub={sub} defaultOpen={i === 0} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HelpPage() {
    const [search, setSearch] = useState('');
    const [activeId, setActiveId] = useState('');

    useEffect(() => {
        const onScroll = () => {
            for (const s of [...SECTIONS].reverse()) {
                const el = document.getElementById(s.id);
                if (el && el.getBoundingClientRect().top <= 120) {
                    setActiveId(s.id); return;
                }
            }
            setActiveId('');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const filtered = search.trim().length > 1
        ? SECTIONS.filter(s =>
            s.title.toLowerCase().includes(search.toLowerCase()) ||
            s.blocks.some(b => 'text' in b && b.text?.toLowerCase().includes(search.toLowerCase())) ||
            s.sub?.some(sub =>
                sub.title.toLowerCase().includes(search.toLowerCase()) ||
                sub.blocks.some(b => 'text' in b && b.text?.toLowerCase().includes(search.toLowerCase()))
            )
        )
        : SECTIONS;

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e6186c]">
                        <BookOpenIcon className="h-5 w-5 text-white" />
                    </div>
                    <h1 className="text-2xl font-semibold text-stone-900">Operations Handbook</h1>
                </div>
                <p className="text-sm text-stone-500 ml-12">Step-by-step instructions for using the Admin Portal</p>
            </div>

            <div className="flex gap-6">
                {/* Sticky sidebar */}
                <aside className="hidden lg:block w-56 flex-shrink-0">
                    <div className="sticky top-24 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
                        <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-widest text-stone-400">Sections</p>
                        <nav className="space-y-0.5">
                            {SECTIONS.map(s => (
                                <button
                                    key={s.id}
                                    onClick={() => scrollTo(s.id)}
                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-all ${
                                        activeId === s.id
                                            ? 'bg-[#fde8f0] font-medium text-[#e6186c]'
                                            : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                                    }`}
                                >
                                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-xs font-bold transition-all ${
                                        activeId === s.id ? 'bg-[#e6186c] text-white' : 'bg-stone-100 text-stone-500'
                                    }`}>
                                        {s.number}
                                    </span>
                                    <span className="leading-snug">{s.title}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Main content */}
                <div className="flex-1 min-w-0 space-y-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search the guide..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full rounded-xl border border-stone-300 bg-white py-3 pl-10 pr-4 text-sm shadow-sm placeholder:text-stone-400 focus:border-[#e6186c] focus:outline-none focus:ring-2 focus:ring-[#e6186c]/20"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400 hover:text-stone-600"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center">
                            <p className="text-stone-500">No sections match your search.</p>
                        </div>
                    ) : (
                        filtered.map(s => <SectionCard key={s.id} section={s} />)
                    )}
                </div>
            </div>
        </div>
    );
}